import { OPENX_AGENT_TOOL } from '@openx/components-openx-agent/components/Home/utils/consts';
import { MenuItemIcon } from '@openx/components-openx-agent/components/icons/MenuItemIcon';
import { useOpenXAgentContext } from '@openx/components-openx-agent/context/useOpenXAgentContext';
import { type PackageRowActionsType, PackageRowActionType } from '@openx/components-package/types';
import { usePackageContext } from '@openx/components-package/utils/context/PackageContext';
import { usePackageActionPermissions } from '@openx/components-package/utils/hooks/usePackageActionPermissions';
import { usePackageDeletePermissions } from '@openx/components-package/utils/hooks/usePackageDeletePermissions';
import { usePackageOperations } from '@openx/components-package/utils/hooks/usePackageOperations';
import { ACL_TYPE } from '@openx/types/aclType';
import type { PackageListItem } from '@openx/types/package';
import type { UsePackageUsedByResponse } from '@openx/types/package/PackageHookTypes';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { OPEN_AUDIENCE_ORG_UID } from '@openx/utils/services/config';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useGetNotAllowedMsg } from '../../../../config';
import { useIsAllowed } from '../../../../configs/permission/utils/useIsAllowed';

export const usePackagesRowActions = (
  packageUsedBy: UsePackageUsedByResponse,
  isCuratedList = false
): PackageRowActionsType => {
  const { t } = useTranslation();
  const { isOaSuborg } = usePackageContext();
  const { isAllowed, accessUser } = useIsAllowed();
  const { setIsOpenXAgentOpen, setActiveTool } = useOpenXAgentContext();
  const {
    canEdit: canEditBase,
    canDelete: canDeleteBase,
    canClone,
    canRead,
  } = usePackageActionPermissions({ isAllowed, accessUser });
  const notAllowedMsg = useGetNotAllowedMsg();
  const navigate = useNavigate();
  const { callPackageAction, loadingAction } = usePackageOperations(); // OA-specific permission wrappers that consider isOaSuborg
  const canEdit = useCallback(
    (pkg: PackageListItem) => {
      if (isOaSuborg && pkg?.is_curated) {
        return false; // OA suborganizations cannot edit curated packages
      }
      return canEditBase(pkg);
    },
    [canEditBase, isOaSuborg]
  );

  const canDelete = useCallback(
    (pkg: PackageListItem) => {
      if (isOaSuborg && pkg?.is_curated) {
        return false; // OA suborganizations cannot delete curated packages
      }
      return canDeleteBase(pkg);
    },
    [canDeleteBase, isOaSuborg]
  );

  const [updatingPackageId, setUpdatingPackageId] = useState('');

  const isLoading = useCallback(
    (packageId: string) => {
      return !!loadingAction && packageId === updatingPackageId;
    },
    [loadingAction, updatingPackageId]
  );

  const { getDeleteActionPermissions } = usePackageDeletePermissions();

  const packageRowActions: PackageRowActionsType = useMemo(
    () => ({
      ...(!isCuratedList && {
        CLONE: {
          allowed: pckg => canClone(pckg),
          'data-test': 'clone',
          label: t('Clone'),
          loading: pckg => isLoading(pckg.id),
          notAllowedMessage: notAllowedMsg,
          onClick: pckg => {
            setUpdatingPackageId(pckg.id);
            callPackageAction({
              action: PackageRowActionType.CLONE,
              packages: [pckg],
              skipModal: true,
            });
          },
        },
      }),
      CREATE_DEAL: {
        allowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.PACKAGE_CREATE)),
        'data-test': 'use-as-template-in-new-deal-button',
        label: t('Use as template in new deal'),
        notAllowedMessage: notAllowedMsg,
        onClick: pckg =>
          navigate('/deals/create', {
            state: {
              packageUid:
                pckg.is_curated && isOaSuborg && pckg.account_uid === OPEN_AUDIENCE_ORG_UID ? pckg.id : pckg.uid,
              useAsTemplate: true,
            },
          }),
      },
      DELETE: {
        allowed: pckg =>
          getDeleteActionPermissions({
            pckg,
            permissionCheck: () => canDelete(pckg),
            packageUsedBy,
            notAllowedMsgDefault: notAllowedMsg,
          }).isAllowed,
        color: 'error',
        danger: 'true',
        'data-test': 'delete',
        label: t('Delete'),
        loading: pckg => isLoading(pckg.id),
        notAllowedMessage: pckg =>
          getDeleteActionPermissions({
            pckg,
            permissionCheck: () => canDelete(pckg),
            packageUsedBy,
            notAllowedMsgDefault: notAllowedMsg,
          }).notAllowedMessage ?? '',
        onClick: pckg => {
          setUpdatingPackageId(pckg.id);
          callPackageAction({
            action: PackageRowActionType.DELETE,
            packages: [pckg],
          });
        },
      },
      DETAILS: {
        allowed: pckg => canRead(pckg),
        'data-test': 'details',
        label: t('Details'),
        notAllowedMessage: notAllowedMsg,
        onClick: pckg => {
          navigate(`/inventory/packages/${pckg.uid}/details${isCuratedList ? '?is_curated=true' : ''}`);
        },
      },
      EDIT: {
        allowed: pckg => canEdit(pckg),
        'data-test': 'edit',
        label: t('Edit'),
        notAllowedMessage: notAllowedMsg,
        onClick: pckg => {
          navigate(`/inventory/packages/${pckg.uid}/edit`);
        },
      },
      USE_SHARED_TARGETING: {
        allowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.PACKAGE_CREATE)),
        'data-test': 'use-shared-targeting',
        label: t('Use Shared Targeting'),
        notAllowedMessage: notAllowedMsg,
        onClick: pckg =>
          navigate('/deals/create', {
            state: {
              packageUid:
                pckg.is_curated && isOaSuborg && pckg.account_uid === OPEN_AUDIENCE_ORG_UID ? pckg.id : pckg.uid,
              useSharedTargeting: true,
            },
          }),
      },
      CHECK_TARGETING: {
        'data-test': 'check-targeting',
        label: t('Check Targeting'),
        onClick: pckg => {
          setActiveTool({
            toolId: OPENX_AGENT_TOOL.TARGETING_EXPLAINER,
            toolData: pckg,
          });
          setIsOpenXAgentOpen(true);
        },
        startIcon: <MenuItemIcon />,
        allowed: isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED)),
        hide: !isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED)),
      },
    }),
    [
      t,
      notAllowedMsg,
      isLoading,
      callPackageAction,
      navigate,
      packageUsedBy,
      isCuratedList,
      getDeleteActionPermissions,
      canClone,
      canDelete,
      canRead,
      canEdit,
      isAllowed,
      isOaSuborg,
      setIsOpenXAgentOpen,
      setActiveTool,
    ]
  );

  return packageRowActions;
};
